import { googleCalendarEventUrl } from '@fewerbags/core';
import type { MealSlot, MealTimeDto, ReminderDto } from '@fewerbags/schema';

/**
 * Household routine, calendars and reminders (D27/D28) — query keys and
 * small pure helpers shared by the settings page, the plan page and the
 * dashboard card.
 *
 * Reminder keys live under the `plans` prefix so the plan screens'
 * `invalidateQueries({ queryKey: ['plans'] })` refreshes them too.
 */
export const scheduleKeys = {
  mealTimes: ['schedule', 'mealTimes'] as const,
  busyBlocks: ['schedule', 'busyBlocks'] as const,
  feeds: ['calendar', 'feeds'] as const,
  exportStatus: ['calendar', 'export'] as const,
  planReminders: (planId: string) => ['plans', 'reminders', planId] as const,
  suggestedReminders: (planId: string) => ['plans', 'reminders', planId, 'suggested'] as const,
  upcomingReminders: (days: number) => ['plans', 'reminders', 'upcoming', days] as const,
};

/** Sunday-first, matching the API's 0 = Sunday. */
export const WEEKDAY_LABEL = [
  'Sunday',
  'Monday',
  'Tuesday',
  'Wednesday',
  'Thursday',
  'Friday',
  'Saturday',
] as const;

/** Display order on the settings page: Monday first. */
export const WEEKDAY_ORDER = [1, 2, 3, 4, 5, 6, 0] as const;

export type WeekGrid = Record<number, Partial<Record<MealSlot, string>>>;

/** API rows → `{ weekday: { slot: 'HH:MM' } }`. */
export function toWeekGrid(rows: readonly MealTimeDto[]): WeekGrid {
  const grid: WeekGrid = {};
  for (const r of rows) grid[r.weekday] = { ...grid[r.weekday], [r.slot]: r.serveAt };
  return grid;
}

/** Grid → API rows, dropping empty times. */
export function fromWeekGrid(grid: WeekGrid): MealTimeDto[] {
  const out: MealTimeDto[] = [];
  for (const [day, slots] of Object.entries(grid)) {
    for (const [slot, serveAt] of Object.entries(slots)) {
      if (serveAt) out.push({ weekday: Number(day), slot: slot as MealSlot, serveAt });
    }
  }
  return out.sort((a, b) => a.weekday - b.weekday || a.serveAt.localeCompare(b.serveAt));
}

const pad = (n: number) => String(n).padStart(2, '0');

/** ISO instant → `YYYY-MM-DDTHH:MM` in the browser's zone, for `<input type="datetime-local">`. */
export function toDateTimeLocal(iso: string): string {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '';
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
}

/** `<input type="datetime-local">` value → ISO instant, or null when empty/invalid. */
export function fromDateTimeLocal(value: string): string | null {
  if (!/^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}$/.test(value)) return null;
  const d = new Date(value);
  return Number.isNaN(d.getTime()) ? null : d.toISOString();
}

/** "Today, 8:00 PM" / "Tomorrow, 8:00 PM" / "Wed 14, 8:00 PM". */
export function formatReminderTime(iso: string, now: Date = new Date()): string {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '';
  const clock = d.toLocaleTimeString(undefined, { hour: 'numeric', minute: '2-digit' });
  const day = (x: Date) => new Date(x.getFullYear(), x.getMonth(), x.getDate()).getTime();
  const diff = Math.round((day(d) - day(now)) / 86_400_000);
  if (diff === 0) return `Today, ${clock}`;
  if (diff === 1) return `Tomorrow, ${clock}`;
  if (diff === -1) return `Yesterday, ${clock}`;
  return `${d.toLocaleDateString(undefined, { weekday: 'short', day: 'numeric' })}, ${clock}`;
}

/** Google Calendar "new event" link for one reminder (15 minutes long). */
export function reminderGoogleUrl(
  reminder: Pick<ReminderDto, 'text' | 'remindAt'>,
  details?: string,
): string {
  const start = new Date(reminder.remindAt);
  return googleCalendarEventUrl({
    title: reminder.text,
    start,
    end: new Date(start.getTime() + 15 * 60_000),
    details: details ?? null,
  });
}

/** "5 minutes ago" style for last-synced labels. */
export function syncedAgo(iso: string | null, now: number = Date.now()): string {
  if (!iso) return 'Not synced yet';
  const mins = Math.round((now - new Date(iso).getTime()) / 60_000);
  if (mins < 1) return 'Synced just now';
  if (mins < 60) return `Synced ${mins} min ago`;
  const hours = Math.round(mins / 60);
  if (hours < 48) return `Synced ${hours} h ago`;
  return `Synced ${Math.round(hours / 24)} days ago`;
}
